/* eslint-disable react-hooks/exhaustive-deps */
import { FC, useState } from "react";
import "./DeeplinkLoginScreen.css";
import letsDoItIcon from "../../assets/images/lets-do-it-icon.svg";
import { useLocation, useNavigate } from "react-router-dom";
import {
  EMAIL_REGEX,
  EXPERIENCE_TYPE,
  USER_ROLE_LIST,
  USER_TYPE,
} from "utils/constants";
import appIcon from "../../assets/images/header-sabehub-app-icon.svg";
import appTextIcon from "../../assets/images/header-sabehub-text-app-icon.svg";
import bannerImage from "../../assets/images/deeplink-images/deeplink-focus-house-image.svg";
import fillingFastIcon from "../../assets/images/deeplink-images/deeplink-filling-fast-icon.svg";
import starIcon from "../../assets/images/communicate-star-icon.svg";
import arrowIcon from "../../assets/images/product-seat-left-arrow.svg";
import { useStorage } from "services/StorageProvider";
import PhoneInput from "react-phone-input-2";
import parsePhoneNumberFromString from "libphonenumber-js";
import { useMutation, useQuery } from "@tanstack/react-query";
import useWSMApi from "services/api/useWSMApi";
import { trackLogin } from "utils/analytics";

const DeepLinkLoginScreen: FC = () => {
  const navigate = useNavigate();
  const { registerUser, updateUserProfile, fetchProduct } = useWSMApi();
  const { updateUserProfileData } = useStorage();
  const location = useLocation();
  const experienceType: string = location.state?.experience_type ?? "";
  const isFocusHousePass = experienceType === EXPERIENCE_TYPE.FOCUS_HOUSE;
  const [currentStep, setCurrentStep] = useState(1);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [mobileNumber, setMobileNumber] = useState("");
  const [phoneBlurred, setPhoneBlurred] = useState(false);
  const [userEmail, setUserEmail] = useState("");
  const isValidPhoneNumber = (number: string) => {
    const normalized = number.replace(/^\+/, "");
    const phoneNumber = parsePhoneNumberFromString("+" + normalized);
    return phoneNumber ? phoneNumber.isValid() : false;
  };
  const [userRole, setUserRole] = useState<string>("");
  const [profileLink, setProfileLink] = useState(() => {
    return sessionStorage.getItem("profileLink") || "";
  });
  const [isFounder, setIsFounder] = useState(() => {
    const value = sessionStorage.getItem("isFounder");
    return value !== null ? JSON.parse(value) : null;
  });
  const validateEmail = (email: string): boolean => {
    return EMAIL_REGEX.test(email);
  };
  const isFirstStepDisabled =
    !firstName.trim() ||
    !mobileNumber.trim() ||
    !isValidPhoneNumber(mobileNumber) ||
    !userEmail.trim() ||
    !validateEmail(userEmail);
  const isSecondStepDisabled = isFocusHousePass
    ? !userRole
    : isFounder === null || !profileLink.trim();

  const { data: productData } = useQuery({
    queryKey: ["get-reservation-data", experienceType],
    queryFn: () => fetchProduct(experienceType),
    enabled: !!experienceType,
  });

  const registerUserMutation = useMutation({
    mutationFn: () => registerUser({ email: userEmail, type: USER_TYPE }),
    onSuccess: (data) => {
      localStorage.setItem("apiAccessToken", data?.accessToken);
      trackLogin();
      updateUserMutation.mutate();
    },
  });

  const updateUserMutation = useMutation({
    mutationFn: () =>
      updateUserProfile({
        first_name: firstName,
        last_name: lastName,
        mobile_number: "+" + mobileNumber,
        profile_link: profileLink,
        ...(isFounder && { is_founder: isFounder }),
        ...(isFocusHousePass && { role: userRole }),
      }),
    onSuccess: (response) => {
      updateUserProfileData(response);
      navigate(-1);
    },
  });

  const handleFirstStepContinueClick = (): void => {
    if (!isFirstStepDisabled) {
      setCurrentStep(2);
    }
  };

  const handleContinueClick = (): void => {
    if (!isSecondStepDisabled) {
      registerUserMutation.mutate();
    }
  };

  return (
    <div className="wms-mobile-container">
      <div className="deeplink-login-screen-container">
        <div className="deeplink-login-header-container">
          <div className="left-container">
            <img src={appIcon} alt="header-app-icon" className="app-image" />
            <img src={appTextIcon} alt="header-text-app-icon" />
          </div>
          <div className="right-container">
            <img src={starIcon} alt="star-icon" />
            {productData?.tag}
          </div>
        </div>
        <div className="deeplink-login-banner-image-container">
          <img src={bannerImage} alt="banner-image" />
        </div>
        <div className="deeplink-login-page-title-container">
          <span>{productData?.title}</span>
          <span className="short-description">{productData?.description}</span>
        </div>
        <div className="deeplink-login-filling-fast-container">
          <div className="left-container">
            <img src={fillingFastIcon} alt="filling-fast-icon" />
            <span>Filling Fast</span>
          </div>
          <img src={arrowIcon} alt="filling-fast-arrow-icon" />
          <div className="right-container">
            <span className="seates">
              {(productData?.total_seats ?? 0) -
                (productData?.reserved_seats ?? 0)}
            </span>
            <span>SEATS LEFT</span>
          </div>
        </div>

        <div className="deeplink-login-cell-bg-container">
          {currentStep === 1 ? (
            <span className="title">
              CLAIM YOUR <span style={{ color: "#FF6B35" }}>SEAT</span>
            </span>
          ) : (
            <span className="title">
              Let's Get <span style={{ color: "#FF6B35" }}>You Inside</span>
            </span>
          )}
          {currentStep === 1 ? (
            <span className="sub-title">You’re seconds from the realm.</span>
          ) : (
            <span className="sub-title">
              Just the basics — you're seconds from the realm.
            </span>
          )}
          <div className="divider" />
          {currentStep === 1 ? (
            <>
              <div className="deeplink-login-dialog-item-container">
                <div className="row-container">
                  <span>
                    First <span className="sub-text">Name</span>
                    <span className="required">*</span>
                  </span>
                  <span>
                    Last <span className="sub-text">Name</span>
                  </span>
                </div>
                <div className="row-container">
                  <input
                    autoFocus
                    placeholder="Enter First Name"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                  />
                  <input
                    placeholder="Enter Last Name"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                  />
                </div>
              </div>
              <div className="deeplink-login-dialog-item-container">
                <span>
                  Phone <span className="sub-text">Number</span>
                  <span className="required">*</span>
                </span>
                <PhoneInput
                  country={"in"}
                  value={mobileNumber}
                  onChange={setMobileNumber}
                  onBlur={() => setPhoneBlurred(true)}
                  countryCodeEditable={false}
                  inputProps={{
                    name: "mobile",
                    required: true,
                  }}
                  containerClass={`deeplink-login-custom-phone${phoneBlurred && !isValidPhoneNumber(mobileNumber) ? " deeplink-login-phone-error" : ""}`}
                  inputClass="deeplink-login-custom-phone-input"
                />
              </div>
              <div className="deeplink-login-dialog-item-container">
                <span>
                  Email <span className="sub-text">Address</span>
                  <span className="required">*</span>
                </span>
                <input
                  type="email"
                  placeholder="youremail@address.com"
                  value={userEmail}
                  onChange={(e) => setUserEmail(e.target.value)}
                />
              </div>
            </>
          ) : (
            <>
              <div className="deeplink-login-dialog-item-container">
                <span>
                  Ur <span className="sub-text">Professional Profile</span>
                  {!isFocusHousePass && <span className="required">*</span>}
                </span>
                <input
                  placeholder="🔗 Enter Website/Linkedin/Instagram Link"
                  value={profileLink}
                  onChange={(e) => setProfileLink(e.target.value)}
                />
              </div>
              <div className="deeplink-login-dialog-item-container">
                <span>
                  {isFocusHousePass ? (
                    <span>
                      Select your <span className="sub-text">role</span>
                    </span>
                  ) : (
                    <span>
                      Are you a{" "}
                      <span className="sub-text">founder/owner ?</span>
                    </span>
                  )}
                  <span className="required">*</span>
                </span>
                {isFocusHousePass ? (
                  <select
                    className="deeplink-login-role-select"
                    value={userRole}
                    onChange={(e) => setUserRole(e.target.value)}
                  >
                    <option value="" disabled>
                      Select your role
                    </option>
                    {USER_ROLE_LIST.map((role) => (
                      <option key={role.value} value={role.value}>
                        {role.name}
                      </option>
                    ))}
                  </select>
                ) : (
                  <div className="button-container">
                    <button
                      className={isFounder === true ? "selected" : ""}
                      onClick={() => setIsFounder(true)}
                    >
                      Yes
                    </button>
                    <button
                      className={isFounder === false ? "selected" : ""}
                      onClick={() => setIsFounder(false)}
                    >
                      No
                    </button>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
        <div
          className="deeplink-login-content-button"
          onClick={
            currentStep === 1
              ? handleFirstStepContinueClick
              : handleContinueClick
          }
          style={{
            opacity: (
              currentStep === 1 ? !isFirstStepDisabled : !isSecondStepDisabled
            )
              ? 1
              : 0.5,
          }}
        >
          {currentStep === 1 ? "Enter Into The Realm" : "Step Inside"}
          <img src={letsDoItIcon} alt="logo" />
        </div>
        <span className="deeplink-login-footer-tagline">
          Powered By{" "}
          <span style={{ color: "#9B5DE5" }}>{productData?.powered_by}</span>
        </span>
      </div>
    </div>
  );
};

export default DeepLinkLoginScreen;
